import * as React from 'react';

import {
  enumWorkOrderSortOption,
  enumWorkOrderStatus,
  WorkOrderSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  Pagination,
  SearchField,
  Button,
  Select,
  ListScreenLayout,
  type ListStatsCardProps,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Plus,
  ClipboardList,
  FileEdit,
  Play,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';

import { $remoteFacilities } from '../../_state/facilities.state';
import { $remoteAllProcessingTypes } from '../../_state/processingTypes.state';
import * as state from '../../_state/workOrdersList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { WorkOrderRecord } from '../../types/workOrder';

import { useWorkOrdersColumns } from './hooks/useWorkOrdersColumns';
import { WorkOrderDialog } from './WorkOrderDialog';

import { $location } from '~/shared/state';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: WorkOrderSortOption;
    desc: WorkOrderSortOption;
  }
> = {
  workOrderNumber: {
    asc: enumWorkOrderSortOption.WorkOrderNumberAsc,
    desc: enumWorkOrderSortOption.WorkOrderNumberDesc,
  },
  status: {
    asc: enumWorkOrderSortOption.StatusAsc,
    desc: enumWorkOrderSortOption.StatusDesc,
  },
  scheduledStart: {
    asc: enumWorkOrderSortOption.ScheduledStartDateAsc,
    desc: enumWorkOrderSortOption.ScheduledStartDateDesc,
  },
  createdAt: {
    asc: enumWorkOrderSortOption.CreatedAtAsc,
    desc: enumWorkOrderSortOption.CreatedAtDesc,
  },
};

export const WorkOrdersListView = () => {
  const t = useTranslations();
  const setLocation = useSetAtom($location);

  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedStatus, setSelectedStatus] = useAtom(state.$selectedStatus);
  const [selectedProcessingType, setSelectedProcessingType] = useAtom(
    state.$selectedProcessingType,
  );
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteWorkOrders,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const { data: processingTypesData } = useAtomValue($remoteAllProcessingTypes);

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const workOrders = data?.workOrders ?? [];
  const total = data?.totalCount ?? 0;
  const emptyPanelProps = {
    title: t.workOrdersView.emptyState.title,
    description: <span>{t.workOrdersView.emptyState.subtitle}</span>,
    centered: true,
  };

  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );
  const processingTypes = React.useMemo(
    () => processingTypesData?.processingTypes ?? [],
    [processingTypesData?.processingTypes],
  );
  const statusOptions = React.useMemo(
    () => [
      { key: 'all', label: t.workOrdersView.filters.allStatuses },
      {
        key: enumWorkOrderStatus.Draft,
        label: t.workOrdersView.statusTypes.Draft,
      },
      {
        key: enumWorkOrderStatus.Open,
        label: t.workOrdersView.statusTypes.Open,
      },
      {
        key: enumWorkOrderStatus.InProgress,
        label: t.workOrdersView.statusTypes.InProgress,
      },
      {
        key: enumWorkOrderStatus.Completed,
        label: t.workOrdersView.statusTypes.Completed,
      },
      {
        key: enumWorkOrderStatus.Cancelled,
        label: t.workOrdersView.statusTypes.Cancelled,
      },
    ],
    [t],
  );
  const processingTypeOptions = React.useMemo(
    () => [
      { key: 'all', label: t.workOrdersView.filters.allProcessingTypes },
      ...processingTypes.map((pt) => ({ key: pt.id, label: pt.name })),
    ],
    [processingTypes, t],
  );
  const facilityOptions = React.useMemo(
    () => [
      { key: 'all', label: t.workOrdersView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );
  const statsColumns = React.useMemo(
    (): ListStatsCardProps['stats'] => [
      {
        key: 'total',
        label: t.workOrdersView.summary.total,
        value: total,
        icon: <ClipboardList />,
        color: 'primary',
      },
      {
        key: 'draft',
        label: t.workOrdersView.summary.draft,
        value: workOrders.filter(
          (wo) => wo.status === enumWorkOrderStatus.Draft,
        ).length,
        icon: <FileEdit />,
        color: 'neutral',
      },
      {
        key: 'open',
        label: t.workOrdersView.summary.open,
        value: workOrders.filter((wo) => wo.status === enumWorkOrderStatus.Open)
          .length,
        icon: <Clock />,
        color: 'primary',
      },
      {
        key: 'inProgress',
        label: t.workOrdersView.summary.inProgress,
        value: workOrders.filter(
          (wo) => wo.status === enumWorkOrderStatus.InProgress,
        ).length,
        icon: <Play />,
        color: 'warning',
      },
      {
        key: 'completed',
        label: t.workOrdersView.summary.completed,
        value: workOrders.filter(
          (wo) => wo.status === enumWorkOrderStatus.Completed,
        ).length,
        icon: <CheckCircle />,
        color: 'success',
      },
      {
        key: 'cancelled',
        label: t.workOrdersView.summary.cancelled,
        value: workOrders.filter(
          (wo) => wo.status === enumWorkOrderStatus.Cancelled,
        ).length,
        icon: <XCircle />,
        color: 'failure',
      },
    ],
    [workOrders, total, t],
  );

  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumWorkOrderSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleRowClick = React.useCallback(
    ({ row }: { row: WorkOrderRecord }) => {
      setLocation({
        pathname: `/production/work-orders/${row.id}`,
      });
    },
    [setLocation],
  );
  const handleCreateClick = React.useCallback(() => {
    setIsDialogOpen(true);
  }, []);
  const handleDialogClose = React.useCallback(() => {
    setIsDialogOpen(false);
  }, []);
  const handleDialogSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);
  const handleStatusChange = React.useCallback(
    (key: string) => {
      setSelectedStatus(key === 'all' ? undefined : key);
      setOffset(0);
    },
    [setSelectedStatus, setOffset],
  );
  const handleProcessingTypeChange = React.useCallback(
    (key: string) => {
      setSelectedProcessingType(key === 'all' ? undefined : key);
      setOffset(0);
    },
    [setSelectedProcessingType, setOffset],
  );
  const handleFacilityChange = React.useCallback(
    (key: string) => {
      setSelectedFacility(key === 'all' ? undefined : key);
      setOffset(0);
    },
    [setSelectedFacility, setOffset],
  );

  const columns = useWorkOrdersColumns({ onRowClick: handleRowClick });

  return (
    <div className="@WorkOrdersListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.workOrdersView.filters.search}
              className="w-64"
            />
            <Select
              value={selectedStatus || 'all'}
              onChange={handleStatusChange}
              options={statusOptions}
              className="w-40"
            />
            <Select
              value={selectedProcessingType || 'all'}
              onChange={handleProcessingTypeChange}
              options={processingTypeOptions}
              className="w-48"
            />
            <Select
              value={selectedFacility || 'all'}
              onChange={handleFacilityChange}
              options={facilityOptions}
              className="w-48"
            />
          </>
        }
        actions={
          <Button icon={<Plus />} color="primary" onPress={handleCreateClick}>
            {t.workOrdersView.createWorkOrder}
          </Button>
        }
        stats={statsColumns}
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<WorkOrderRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={workOrders}
          rowKey="id"
          aria-label={t.workOrdersView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.workOrdersView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <WorkOrderDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
      />
    </div>
  );
};
